/*
 * Copyright Higharc Inc. 2019
 */
import { Vector3 } from "three";
import { BitmapUtil } from "./BitmapUtil";
import { CubeCameraUtil } from "./CubeCameraUtil";
import { TetrahedronCamera } from "./TetrahedronCamera";

export const IrradianceMapUtil = {
  tetrahedronMapApproximateCosineConvolution: function (tetraFaces, tetraResolution, numChannels, direction, result) {
    const sampleDelta = (Math.PI * 1.00001) / tetraResolution;

    const faceIndexAndUV = { faceIndex: 0, x: 0, y: 0 };

    let numSamples = 0;

    for (let i = 0; i < numChannels; ++i) {
      result[i] = 0;
    }

    // Reference: https://learnopengl.com/PBR/IBL/Diffuse-irradiance
    const right = new Vector3(0, 1, 0);
    right.cross(direction);
    right.normalize();
    const up = new Vector3(direction.x, direction.y, direction.z);
    up.cross(right);
    up.normalize();
    const sampleVec = new Vector3(0, 0, 0);
    for (let theta = 0.0; theta < 0.5 * Math.PI; theta += sampleDelta) {
      const sinTheta = Math.sin(theta);
      const cosTheta = Math.cos(theta);
      const sampleMul = Math.cos(theta) * Math.sin(theta);
      for (let phi = 0.0; phi < 2.0 * Math.PI; phi += sampleDelta) {
        const tangentSampleX = sinTheta * Math.cos(phi);
        const tangentSampleY = sinTheta * Math.sin(phi);
        const tangentSampleZ = cosTheta;
        sampleVec.copy(right);
        sampleVec.multiplyScalar(tangentSampleX);
        sampleVec.addScaledVector(up, tangentSampleY);
        sampleVec.addScaledVector(direction, tangentSampleZ);

        TetrahedronCamera.directionToFaceIndexAndUV(sampleVec, faceIndexAndUV);
        const pixelIndex = BitmapUtil.nearestSampleIndex(tetraResolution, tetraResolution, faceIndexAndUV);
        for (let i = 0; i < numChannels; ++i) {
          result[i] += tetraFaces[faceIndexAndUV.faceIndex][pixelIndex * numChannels + i] * sampleMul;
        }

        numSamples += 1;
      }
    }

    for (let i = 0; i < numChannels; ++i) {
      result[i] *= Math.PI / numSamples;
    }
  },

  irradianceCubeMapFromRadianceTetrahedronMap: function (radianceFaces, tetraResolution, cubeResolution, numChannels) {
    const irradianceFaces: Float32Array[] = [];

    const texelHalfWidth = 1.0 / cubeResolution;

    const tempConvolutionResult = new Float32Array(numChannels);

    const targetDirection = new Vector3(0, 0, 0);

    for (let faceIndex = 0; faceIndex < 6; ++faceIndex) {
      const targetFace = new Float32Array(cubeResolution * cubeResolution * numChannels);
      let targetTexelCenterX = -1.0 + texelHalfWidth;
      for (let targetIntX = 0; targetIntX < cubeResolution; ++targetIntX) {
        let targetTexelCenterY = 1.0 - texelHalfWidth;
        for (let targetIntY = 0; targetIntY < cubeResolution; ++targetIntY) {
          const targetRedIndex = (targetIntY * cubeResolution + targetIntX) * numChannels;

          CubeCameraUtil.getCubeMapTexelDirection(faceIndex, targetTexelCenterX, targetTexelCenterY, targetDirection);

          IrradianceMapUtil.tetrahedronMapApproximateCosineConvolution(
            radianceFaces,
            tetraResolution,
            numChannels,
            targetDirection,
            tempConvolutionResult,
          );

          for (let i = 0; i < numChannels; ++i) {
            targetFace[targetRedIndex + i] = tempConvolutionResult[i];
          }

          targetTexelCenterY -= texelHalfWidth * 2.0;
        }
        targetTexelCenterX += texelHalfWidth * 2.0;
      }
      irradianceFaces.push(targetFace);
    }
    return irradianceFaces;
  },

  // This computes a single cosine convolution on a cube map.
  cubeMapCosineConvolution: function (
    cubeMapFaces,
    cubeResolution,
    numChannels,
    direction,
    directionFaceIndex,
    solidAngleCache,
    texelDirectionCache,
    result,
  ) {
    for (let i = 0; i < numChannels; ++i) {
      result[i] = 0;
    }

    const oppositeFaceIndex = directionFaceIndex % 2 === 0 ? directionFaceIndex + 1 : directionFaceIndex - 1;

    let totalWeight = 0;
    let cubePixelIndex = 0;
    for (let faceIndex = 0; faceIndex < 6; ++faceIndex) {
      if (faceIndex === oppositeFaceIndex) {
        cubePixelIndex += cubeResolution * cubeResolution;
        continue;
      }
      const face = cubeMapFaces[faceIndex];
      let pixelIndex = 0;
      let sourceIndex = 0;
      for (let intY = 0; intY < cubeResolution; ++intY) {
        for (let intX = 0; intX < cubeResolution; ++intX) {
          const sourceDirection = texelDirectionCache[cubePixelIndex];
          const directionDot = sourceDirection.dot(direction);
          if (directionDot > 0) {
            const weight = directionDot * solidAngleCache[pixelIndex];
            for (let i = 0; i < numChannels; ++i) {
              result[i] += face[sourceIndex] * weight;
              ++sourceIndex;
            }
            totalWeight += weight;
          } else {
            sourceIndex += numChannels;
          }

          ++pixelIndex;
          ++cubePixelIndex;
        }
      }
    }

    for (let i = 0; i < numChannels; ++i) {
      result[i] /= totalWeight;
    }
  },

  // This applies a cosine convolution filter to the input cube map.
  // radianceFaces: list of cube map face data in the following order: +x, -x, +y, -y, +z, -z.
  // returns a similar list with the filtered faces.
  irradianceCubeMapFromRadianceCubeMap: function (
    radianceFaces: Float32Array[],
    targetCubeResolution: number,
    numChannels: number,
    sourceCubeResolution: number = targetCubeResolution,
  ): Float32Array[] {
    const irradianceFaces: Float32Array[] = [];

    const solidAngleCache = CubeCameraUtil.generateSolidAngleCache(sourceCubeResolution);
    const targetTexelDirectionCache = CubeCameraUtil.generateTexelDirectionCache(targetCubeResolution);
    let sourceTexelDirectionCache = targetTexelDirectionCache;
    if (sourceCubeResolution !== targetCubeResolution) {
      sourceTexelDirectionCache = CubeCameraUtil.generateTexelDirectionCache(sourceCubeResolution);
    }

    const tempConvolutionResult = new Float32Array(numChannels);

    let cubePixelIndex = 0;
    for (let faceIndex = 0; faceIndex < 6; ++faceIndex) {
      const targetFace = new Float32Array(targetCubeResolution * targetCubeResolution * numChannels);
      for (let targetIntY = 0; targetIntY < targetCubeResolution; ++targetIntY) {
        for (let targetIntX = 0; targetIntX < targetCubeResolution; ++targetIntX) {
          const targetRedIndex = (targetIntY * targetCubeResolution + targetIntX) * numChannels;

          const targetDirection = targetTexelDirectionCache[cubePixelIndex];

          IrradianceMapUtil.cubeMapCosineConvolution(
            radianceFaces,
            sourceCubeResolution,
            numChannels,
            targetDirection,
            faceIndex,
            solidAngleCache,
            sourceTexelDirectionCache,
            tempConvolutionResult,
          );

          for (let i = 0; i < numChannels; ++i) {
            targetFace[targetRedIndex + i] = tempConvolutionResult[i];
          }

          ++cubePixelIndex;
        }
      }
      irradianceFaces.push(targetFace);
    }
    return irradianceFaces;
  },
};
